import { nowEpochMilliseconds } from "@nijiurachan/js/util/time";
import {
  create,
  type Mutate,
  type StoreApi,
  type UseBoundStore,
} from "zustand";
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from "zustand/middleware";
import { resolveSafePersistStorage } from "@/shared/lib/safePersistStorage";

export const OWN_POSTS_STORAGE_KEY = "nijiurachan-own-posts-v1";

const INITIAL_RESET_TOKEN = "initial-v1";
let fallbackResetTokenSequence = 0;

interface OwnPostState {
  ownPostIds: string[];
  ownPostIdSet: ReadonlySet<string>;
  resetToken: string;
  addOwnPost: (postId: string) => void;
  reconcileOwnPostState: (persistedState: PersistedOwnPostState) => void;
  clearOwnPosts: () => void;
}

interface PersistedOwnPostState {
  ownPostIds: string[];
  resetToken: string;
}

export type OwnPostStore = UseBoundStore<
  Mutate<StoreApi<OwnPostState>, [["zustand/persist", unknown]]>
>;

/** Successfully submitted post IDs remain recognizable in live and archived threads. */
export function createOwnPostStore(storage?: StateStorage): OwnPostStore {
  const safeStorage = createJsonSafeStorage(resolveSafePersistStorage(storage));

  return create<OwnPostState>()(
    persist(
      (set, get): OwnPostState => ({
        ownPostIds: [],
        ownPostIdSet: new Set<string>(),
        resetToken: INITIAL_RESET_TOKEN,
        addOwnPost: (postId: string): void => {
          if (postId.length === 0) return;
          const latestPersistedState =
            readLatestPersistedOwnPostState(safeStorage);
          const currentState = get();
          const resetToken =
            latestPersistedState !== undefined &&
            latestPersistedState.resetToken !== currentState.resetToken
              ? latestPersistedState.resetToken
              : currentState.resetToken;
          const ownPostIds = mergeOwnPostIdGroups(
            resetToken === currentState.resetToken
              ? currentState.ownPostIds
              : [],
            latestPersistedState?.resetToken === resetToken
              ? latestPersistedState.ownPostIds
              : [],
            [postId],
          );
          if (
            resetToken === currentState.resetToken &&
            sameItems(currentState.ownPostIds, ownPostIds) &&
            (latestPersistedState === undefined ||
              (latestPersistedState.resetToken === resetToken &&
                sameItems(latestPersistedState.ownPostIds, ownPostIds)))
          ) {
            return;
          }
          set({ ownPostIds, ownPostIdSet: new Set(ownPostIds), resetToken });
        },
        reconcileOwnPostState: (
          persistedState: PersistedOwnPostState,
        ): void => {
          const currentState = get();
          const sameResetToken =
            currentState.resetToken === persistedState.resetToken;
          const ownPostIds = sameResetToken
            ? mergeOwnPostIdGroups(
                currentState.ownPostIds,
                persistedState.ownPostIds,
              )
            : persistedState.ownPostIds;
          if (
            sameResetToken &&
            sameItems(currentState.ownPostIds, ownPostIds) &&
            sameItems(persistedState.ownPostIds, ownPostIds)
          ) {
            return;
          }
          set({
            ownPostIds,
            ownPostIdSet: new Set(ownPostIds),
            resetToken: persistedState.resetToken,
          });
        },
        clearOwnPosts: (): void => {
          set({
            ownPostIds: [],
            ownPostIdSet: new Set<string>(),
            resetToken: createResetToken(),
          });
        },
      }),
      {
        name: OWN_POSTS_STORAGE_KEY,
        version: 1,
        storage: createJSONStorage(() => safeStorage),
        partialize: (state: OwnPostState): PersistedOwnPostState => ({
          ownPostIds: state.ownPostIds,
          resetToken: state.resetToken,
        }),
        merge: (persistedState: unknown, currentState: OwnPostState) => {
          const restoredState = readOwnPostState(persistedState) ?? {
            ownPostIds: [],
            resetToken: INITIAL_RESET_TOKEN,
          };
          return {
            ...currentState,
            ...restoredState,
            ownPostIdSet: new Set(restoredState.ownPostIds),
          };
        },
      },
    ),
  );
}

export const useOwnPostStore: OwnPostStore = createOwnPostStore();

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event: StorageEvent): void => {
    if (event.key !== null && event.key !== OWN_POSTS_STORAGE_KEY) return;
    const latestPersistedState = readCurrentPersistedOwnPostState();
    if (latestPersistedState === undefined) return;
    const storeState = useOwnPostStore.getState();
    if (latestPersistedState === null) {
      storeState.clearOwnPosts();
      return;
    }
    storeState.reconcileOwnPostState(latestPersistedState);
  });
}

function readOwnPostState(value: unknown): PersistedOwnPostState | undefined {
  if (!isRecord(value) || !Array.isArray(value.ownPostIds)) return undefined;
  const resetToken = Object.hasOwn(value, "resetToken")
    ? value.resetToken
    : INITIAL_RESET_TOKEN;
  if (!isNonEmptyString(resetToken)) return undefined;
  return {
    ownPostIds: mergeOwnPostIdGroups(value.ownPostIds.filter(isNonEmptyString)),
    resetToken,
  };
}

function readLatestPersistedOwnPostState(
  storage: StateStorage,
): PersistedOwnPostState | undefined {
  const serialized = storage.getItem(OWN_POSTS_STORAGE_KEY);
  if (typeof serialized !== "string") return undefined;
  return readPersistedOwnPostState(serialized);
}

function readCurrentPersistedOwnPostState():
  | PersistedOwnPostState
  | null
  | undefined {
  try {
    const serialized = globalThis.localStorage.getItem(OWN_POSTS_STORAGE_KEY);
    return serialized === null ? null : readPersistedOwnPostState(serialized);
  } catch {
    return undefined;
  }
}

function readPersistedOwnPostState(
  serialized: string,
): PersistedOwnPostState | undefined {
  try {
    const persisted: unknown = JSON.parse(serialized);
    if (
      !isRecord(persisted) ||
      persisted.version !== 1 ||
      !isRecord(persisted.state)
    ) {
      return undefined;
    }
    return readOwnPostState(persisted.state);
  } catch {
    return undefined;
  }
}

function createResetToken(): string {
  try {
    const crypto = globalThis.crypto;
    if (typeof crypto?.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // Use the local fallback when the browser does not expose crypto.
  }
  fallbackResetTokenSequence += 1;
  return `${nowEpochMilliseconds().toString(36)}-${fallbackResetTokenSequence.toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function mergeOwnPostIdGroups(
  ...groups: readonly (readonly string[])[]
): string[] {
  return [...new Set(groups.flat().filter(isNonEmptyString))].sort();
}

function sameItems(left: readonly string[], right: readonly string[]): boolean {
  return (
    left.length === right.length &&
    left.every((item, index) => item === right[index])
  );
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function createJsonSafeStorage(storage: StateStorage): StateStorage {
  return {
    getItem(name: string): string | null | Promise<string | null> {
      try {
        const value = storage.getItem(name);
        return value instanceof Promise
          ? value.then(ignoreInvalidJson, () => null)
          : ignoreInvalidJson(value);
      } catch {
        return null;
      }
    },
    setItem(name: string, value: string): unknown {
      return storage.setItem(name, value);
    },
    removeItem(name: string): unknown {
      return storage.removeItem(name);
    },
  };
}

function ignoreInvalidJson(value: string | null): string | null {
  if (value === null) return null;
  try {
    JSON.parse(value);
    return value;
  } catch {
    return null;
  }
}
